import JobsAPI from './api/jobsAPI';
import {
  INFO_STEP_FIELD_REFS,
  INITIAL_INFO_ERRORS_STATE,
  INITIAL_INFO_STATE,
  INITIAL_JOB_GROUP_ERRORS_STATE,
  INITIAL_JOB_GROUP_STATE,
  JOBS_INFO_STEP_ERROR_MESSAGES,
  VALIDATE_JOB_INFO_STEP_FIELDS_FOR_IN_VALID_CHARS,
  PAY_TYPE,
} from './constants/jobs';
import {
  containsInvalidCharacters,
  getInvalidCharacters,
  getInvalidCharactersErrorMessage,
} from './validation';
import { omit } from './utils';

export const validateInfoStepData = (
  data = {},
  setErrors = () => {},
  setErrorMsg = () => {},
  scrollTo = () => {},
  payType,
) => {
  setErrorMsg({});
  let hasEmptyFields = false;
  let isPayRangeInvalid = false;
  let hasPrintableCharacters = true;
  const errorsState = JSON.parse(JSON.stringify(INITIAL_INFO_ERRORS_STATE));
  const errorMsg = {};
  const jobState = { ...data };
  const payMin = parseFloat(jobState.pay_minimum);
  const payMax = parseFloat(jobState.pay_maximum);

  Object.keys(jobState).forEach((val) => {
    if (jobState[val]?.trim) {
      jobState[val] = jobState[val]?.trim();
    }
  });

  const totalEmptyFields = Object.keys(omit(['pay_minimum', 'pay_maximum', 'pay_rate'], INITIAL_INFO_STATE)).filter(val => !jobState[val]);

  totalEmptyFields.forEach((val) => {
    errorsState[val] = true;
    errorMsg[val] = JOBS_INFO_STEP_ERROR_MESSAGES[val].required();
  });

  if ((payMin && !payMax) || (!payMin && payMax)) {
    if (payMin) {
      errorsState.pay_maximum = true;
      errorMsg.pay_maximum = 'Enter pay max';
      if (payMin === 0) {
        errorsState.pay_minimum = true;
        errorMsg.pay_minimum = 'Invalid pay min';
      }
    } else {
      errorsState.pay_minimum = true;
      errorMsg.pay_minimum = 'Enter pay min';
    }
    isPayRangeInvalid = true;
  } else if (payMin === 0 || payMax === 0) {
    if (payMin === 0) {
      errorsState.pay_minimum = true;
      errorMsg.pay_minimum = 'Invalid pay min';
    }
    if (payMax === 0) {
      errorsState.pay_maximum = true;
      errorMsg.pay_maximum = `${payType !== PAY_TYPE.SET_WAGE ? 'Invalid pay max' : 'Invalid pay'}`;
    }
  }
  if (payType !== PAY_TYPE.SET_WAGE && payMin !== 0 && payMax !== 0 && (payMax <= payMin)) {
    errorsState.pay_range = true;
    errorMsg.pay_range = 'Pay max must be greater than pay min';
    isPayRangeInvalid = true;
  }

  if (jobState?.job_address && jobState?.job_address?.postalCode === undefined) {
    errorsState.zip = true;
    errorsState.job_address = true;
    hasEmptyFields = true;
    errorMsg.job_address = JOBS_INFO_STEP_ERROR_MESSAGES.job_address.required(true);
  }

  if (totalEmptyFields.length) {
    hasEmptyFields = true;
  }

  VALIDATE_JOB_INFO_STEP_FIELDS_FOR_IN_VALID_CHARS.forEach((val) => {
    const inValidChar = containsInvalidCharacters(jobState[val], val === 'job_description');
    if (inValidChar) {
      const invalidChars = getInvalidCharacters(jobState[val], val === 'job_description');
      errorsState[val] = true;
      errorMsg[val] = getInvalidCharactersErrorMessage(invalidChars);
      hasPrintableCharacters = false;
    }
  });

  setErrors(errorsState);
  setErrorMsg(errorMsg);

  const isValidated = !hasEmptyFields && !isPayRangeInvalid && payMin !== 0 && payMax !== 0 && hasPrintableCharacters;
  if (!isValidated) {
    scrollTo(errorsState);
  }
  return isValidated;
};

export const validateJobGroupData = (data = {}, setErrors = () => {}, scrollTo = () => {}) => {
  let hasEmptyFields = false;
  const errorsState = JSON.parse(JSON.stringify(INITIAL_JOB_GROUP_ERRORS_STATE));
  const groupState = { ...data };
  Object.keys(groupState).forEach((val) => {
    if (groupState[val]?.trim) {
      groupState[val] = groupState[val]?.trim();
    }
  });

  const totalEmptyFields = Object.keys(omit(['traffic_type', 'sub_organization_id'], INITIAL_JOB_GROUP_STATE)).filter(val => !groupState[val]);

  totalEmptyFields.forEach((val) => {
    errorsState[val] = true;
  });

  if (totalEmptyFields.length) {
    hasEmptyFields = true;
  }

  setErrors(errorsState);

  const isValidated = !hasEmptyFields;
  if (!isValidated) {
    scrollTo(errorsState);
  }
  return isValidated;
};

const executeScroll = (ref) => {
  if (ref) {
    ref.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }
};

export const scrollToFirstErrorField = (refs = {}) => (errorState = {}) => {
  let scrolledToItem = false;
  Object.keys(INFO_STEP_FIELD_REFS).forEach((value) => {
    if (errorState[value] && !scrolledToItem) {
      executeScroll(refs[value].current);
      scrolledToItem = true;
    }
  });
};

export const formatCandidates = candidates => candidates.map(item => ({
  id: item.id,
  name: `${item.first_name} ${item.last_name}`,
  email: item.email,
}));

export const getJobcasePartner = partners => partners.find(partner => partner.is_primary);

export const getPayType = data => (!!data?.pay_maximum && !!data?.pay_minimum) && data?.pay_minimum === data?.pay_maximum;

export const getJobCSVData = async (queryParams) => {
  const extractLines = text => text.split('\n');
  const extractColumnValues = text => text.split(',');
  const response = await JobsAPI().getJobsInCSV(queryParams);
  const lines = extractLines(response);
  const data = [];
  data.push([lines[0]]);

  for (let i = 1; i < lines.length; i += 1) {
    data.push([...extractColumnValues(lines[i])]);
  }

  return data;
};

export const getJobReportingCSVData = async (query, variables) => {
  const extractLines = text => text.split('\n');
  const extractColumnValues = text => text.split(',');
  const response = await JobsAPI().getJobReportingInCSV({
    query,
    variables,
  });
  const lines = extractLines(response);
  const data = [];
  data.push([lines[0]]);

  for (let i = 1; i < lines.length; i += 1) {
    data.push([...extractColumnValues(lines[i])]);
  }

  return data;
};

export default {
  validateInfoStepData,
  getJobcasePartner,
  getPayType,
  getJobReportingCSVData,
};
